import { Modal } from 'components'
import FormInfo from 'components/DueDiligenceList/FormInfo'
import moment from 'moment'
import MUIDataTable from 'mui-datatables'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { get } from 'services/api'
import { formatToCPF } from 'brazilian-values'

import { reportError } from 'utils/errorReporter'
import { extractOnlyNumbers } from 'utils/functions'
import { CircularProgress } from '@mui/material'
import TableLoadingAnimation from 'components/TableLoadingAnimation'
import { debounce } from 'lodash'
import { columns } from './columns'
import generateOptions from './generateOptions'

import styles from './styles'

function DueDiligenceList() {
  const classes = styles()
  const token = useSelector((state) => state.auth.access_token)

  const [data, setData] = useState([])
  const [open, setOpen] = useState(false)
  const [currentFormId, setCurrentFormId] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [totalItems, setTotalItems] = useState(0)
  const [query, setQuery] = useState('')
  const [showOnlyFilled, setShowOnlyFilled] = useState(null)
  const [loading, setLoading] = useState(false)
  const [firstLoading, setFirstLoading] = useState(true)

  const handleView = (id) => {
    setCurrentFormId(id)
    setOpen(true)
  }

  const handleClose = () => {
    setCurrentFormId(null)
    setOpen(false)
  }

  const loadData = useCallback(
    async (currentPage, currentSize) => {
      let url = `/due_diligence/pf/form/all?size=${currentSize}&page=${currentPage + 1}`
      if (query) {
        const queryNumbers = extractOnlyNumbers(query)

        if (queryNumbers) {
          url = url.concat(`&person_cpf=${queryNumbers}`)
        } else {
          url = url.concat(`&person_name=${query}`)
        }
      }

      if (showOnlyFilled !== null) {
        url = url.concat(`&filled=${showOnlyFilled}`)
      }
      const req_options = {
        url: url,
        token: token,
      }

      try {
        setLoading(true)
        const response = await get(req_options)
        const responseData = response.data

        if (responseData?.items) {
          const tableData = responseData.items.map((row) => ({
            ...row,
            actions: {
              uuid: row.uuid,
              viewAction: handleView,
            },
            owner_name: row?.owner?.person?.name,
            owner_email: row?.owner?.prospect_input?.email,
            owner_cpf: formatToCPF(row?.owner?.person?.cpf || ''),
          }))

          setData(tableData)
          setTotalItems(responseData.total)
        } else {
          setData([])
          setTotalItems(0)
        }
      } catch (error) {
        reportError(error)
        toast.error('Houve um erro ao carregar os dados')
      } finally {
        setLoading(false)
        setFirstLoading(false)
      }
    },
    [token, query, showOnlyFilled],
  )

    useEffect(() => {
    loadData(page, rowsPerPage)
  }, [loadData, page, rowsPerPage])

  const handleSearchDebounced = useMemo(
    () =>
      debounce((q) => {
        setQuery(q)
        setPage(0)
      }, 350),
    [token],
  )


  const filename = `due_diligence_list_${moment().format('DD_MM_YYYY_HH_mm_ss')}`
  const baseOptions = generateOptions(filename)

  const tableOptions = {
    ...baseOptions,
    serverSide: true,
    count: totalItems,
    page: page,
    rowsPerPage: rowsPerPage,
    selectableRows: 'none',
    textLabels: {
      body: {
        noMatch: loading ? 'Carregando...' : 'Nenhum registro encontrado',
      },
    },
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changePage':
          setPage(tableState.page)
          break
        case 'changeRowsPerPage':
          setRowsPerPage(tableState.rowsPerPage)
          setPage(0)
          break

        case 'search':
          handleSearchDebounced(tableState.searchText || '')
          break
        case 'resetFilters':
          setQuery('')
          setPage(0)
          break
        case 'filterChange':
          if (tableState.filterList[5]) {
            if (!tableState.filterList[5][0]) {
              setShowOnlyFilled(null)
            } else {
              setShowOnlyFilled(tableState.filterList[5][0] === 'Concluídos')
            }
          } else {
            setShowOnlyFilled(null)
          }

          setPage(0)
          break
        default:
          break
      }
    },
  }

  return (
    <div className={classes.root}>
      <div className={classes.textContent}>
        <h1>Formulários Due Diligence</h1>
        <p>Nesta tela é possível visualizar e buscar formulários de resposta ao Due Diligence.</p>
      </div>
      <div className={classes.tableWrapper} style={{ position: 'relative' }}>
        {/* O loader fica absoluto sobre a tabela */}
        {loading && !firstLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10,
            }}
          >
            {data.length === 0 ? null : <CircularProgress />}
          </div>
        )}
        <div className={classes.panel}>
          {loading && firstLoading ? (
            <TableLoadingAnimation />
          ) : (
            <MUIDataTable data={data} columns={columns} options={tableOptions} />
          )}
        </div>
      </div>
      {open && currentFormId && (
        <Modal title="Respostas do formulário Due Diligence" open={open} onClose={handleClose}>
          <FormInfo id={currentFormId} />
        </Modal>
      )}
    </div>
  )
}

export default DueDiligenceList
